import React from "react";
import {
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";

const currency = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

function PayslipCard({ payslip }) {
  if (!payslip) return null;

  const employee = payslip.employee || {};
  const period = payslip.period || {};
  const earnings = payslip.earnings || {};
  const deductions = payslip.deductions || {};
  const absences = deductions.absences || {};
  const totals = payslip.totals || {};

  const fmtMoney = (value) => currency.format(Number(value || 0));
  const fmtQty = (value) => (value === null || value === undefined || value === "" ? "-" : String(value));
  const basicQtyNum = Number(earnings.days_worked);
  const hasBasicQty = Number.isFinite(basicQtyNum) && basicQtyNum > 0;
  const dailyRateNum = Number(earnings.daily_rate);
  const inferredRate = hasBasicQty ? (Number(earnings.basic_pay || 0) / basicQtyNum) : NaN;
  const basicRateNum = Number.isFinite(dailyRateNum) && dailyRateNum > 0 ? dailyRateNum : inferredRate;

  const earningsRows = [
    {
      label: "Basic Pay",
      qty: hasBasicQty ? fmtQty(basicQtyNum) : "-",
      rate: Number.isFinite(basicRateNum) && basicRateNum > 0 ? fmtMoney(basicRateNum) : "-",
      amount: fmtMoney(earnings.basic_pay)
    },
    {
      label: "Overtime",
      qty: "-",
      rate: "-",
      amount: fmtMoney(earnings.overtime_pay)
    },
    {
      label: "Holiday Pay",
      qty: "-",
      rate: "-",
      amount: fmtMoney(earnings.holiday_pay)
    },
    {
      label: "Allowances",
      qty: "-",
      rate: "-",
      amount: fmtMoney(earnings.allowances)
    }
  ];

  const deductionRows = [
    { label: "SSS Contribution", amount: fmtMoney(deductions.sss) },
    { label: "PhilHealth", amount: fmtMoney(deductions.philhealth) },
    { label: "Pag-IBIG", amount: fmtMoney(deductions.pagibig) },
    { label: "Tax", amount: fmtMoney(deductions.tax) },
    { label: "Late / Undertime", amount: fmtMoney(deductions.late) },
    { label: `Absences (${fmtQty(absences.days)} days)`, amount: fmtMoney(absences.amount) },
    { label: "Other Deductions", amount: fmtMoney(deductions.others) }
  ];

  return (
    <Card className="payslip-print" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {String(employee.full_name || "").toUpperCase()}
          </Typography>
          <Typography variant="body2">ID No.: {employee.employee_no || ""}</Typography>
          <Typography variant="body2">Position: {employee.position_type || ""}</Typography>
        </Box>
        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Payslip for {period.cutoff_label || ""}
          </Typography>
          <Typography variant="body2">
            Period: {period.start || ""} - {period.end || ""}
          </Typography>
          {period.pay_date && (
            <Typography variant="body2">
              Pay Date: {period.pay_date}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Earnings
      </Typography>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {earningsRows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.qty}</TableCell>
                <TableCell align="right">{row.rate}</TableCell>
                <TableCell align="right">{row.amount}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>GROSS PAY</TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmtMoney(earnings.gross_pay)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Deductions
      </Typography>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductionRows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.amount}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>TOTAL DEDUCTIONS</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmtMoney(deductions.total_deductions)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Typography className="net-pay" variant="h6" sx={{ fontWeight: 700 }}>
          NET PAY: {fmtMoney(totals.net_pay)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No. of absences this period: {fmtQty(absences.days)}
        </Typography>
      </Box>
    </Card>
  );
}

export default PayslipCard;
